import { colors } from '@dixa/lib-shared-tokens';
import type {
  ChartInstrument,
  InstrumentDataGranularity,
} from 'lib-analytics-types';
import { formatInstrumentNumber } from 'lib-analytics-ui';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { XAxisProps, YAxisProps } from 'recharts';

import { useAnalyticsState } from 'app/views/layout/views/analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';

import { useInstrumentComparison } from '../../../../../comparison/instrumentComparison.context';
import { tickCount, tickProps } from '../../../utils/appearance';
import {
  formatDateCategory,
  formatTextCategory,
} from '../../../utils/formatting';

type UseChartInstrumentAxesOptions = {
  instrument: ChartInstrument;
  granularity?: InstrumentDataGranularity;
};

type UseChartInstrumentAxesResult = {
  xAxisProps: XAxisProps;
  yAxisProps: YAxisProps;
};

/**
 * A hook that returns the props for the X and Y axes components of a chart.
 *
 * The hook is used to abstract the logic and UI of the chart axes as you would
 * usually do with custom components, because the Recharts library does not
 * allow wrapping their chart components into custom components.
 */
export const useChartInstrumentAxes = ({
  instrument,
  granularity,
}: UseChartInstrumentAxesOptions): UseChartInstrumentAxesResult => {
  const {
    settings: { timezone },
  } = useAnalyticsState();

  const { data, layout } = instrument;

  const isHorizontal = layout === 'horizontal';
  const isVertical = layout === 'vertical';
  const verticalYAxisWidth = 192;

  const comparison = useInstrumentComparison();

  const categoryTickInterval = useMemo(() => {
    // Show all category ticks in vertical charts where they can't collide horizontally.
    if (isVertical) return 0;

    const isEven = data.categories.length % 2 === 0;

    switch (granularity) {
      // There are always an even number of categories, so we preserve the first tick in order to
      // ensure that hours are shown in even intervals e.g. 08:00, 10:00, 12:00 ... 22:00.
      case 'H':
        return 'preserveStart';
      // When the number of days is even, we preserve the last tick in order to ensure that
      // days are shown in equal intervals e.g. Apr 2, Apr 4, Apr 6 ... Apr 30. When uneven, we
      // preserve the first tick, resulting in equal intervals e.g. Mar 1, Mar 4, Mar 7 ... Mar 31.
      case 'D':
      case 'W':
        return isEven ? 'preserveEnd' : 'preserveStart';
      default:
        return 'preserveStartEnd';
    }
  }, [data.categories.length, granularity, isVertical]);

  const categoryProps: XAxisProps | YAxisProps = {
    dataKey: 'name',
    width: isVertical ? verticalYAxisWidth : 0,
    tickLine: {
      stroke: colors.neutral['200'],
    },
    axisLine: {
      stroke: colors.neutral['200'],
    },
    tickSize: 6,
    tickMargin: 8,
    interval: categoryTickInterval,
    tick: tickProps,
    tickFormatter: (value: string) =>
      moment(value, moment.ISO_8601).isValid()
        ? formatDateCategory(value, granularity, { timezone })
        : formatTextCategory(value, { length: verticalYAxisWidth / 10 }),
  };

  const numberProps: XAxisProps | YAxisProps = {
    domain: [0, 'auto'],
    allowDecimals: false,
    tickLine: false,
    axisLine: false,
    tick: tickProps,
    ticks: comparison?.syncOptions?.chart?.ticks,
    tickCount,
    tickFormatter: (value: number) =>
      formatInstrumentNumber(value, data.series[0].options?.format ?? 'number'),
  };

  const xAxisProps: XAxisProps = isHorizontal
    ? { type: 'category', ...(categoryProps as XAxisProps) }
    : { type: 'number', ...(numberProps as XAxisProps) };

  const yAxisProps: YAxisProps = isHorizontal
    ? { type: 'number', ...(numberProps as YAxisProps) }
    : { type: 'category', ...(categoryProps as YAxisProps) };

  return { xAxisProps, yAxisProps };
};
